import { Big } from 'big.js';
import { DisplayData, TenantJobLine } from '~/lib/model';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import BreJob from '~/tenants/bre/model/BreJob';
import { BreOrderType } from '~/tenants/bre/model/enums';
import { BreDesignPay, BreDesignRate } from '~/tenants/bre/performable/print/design/BreDesignData';
import BrePostcardsPrintOnlyConfig
  from '~/tenants/bre/performable/print/postcards/printonly/BrePostcardsPrintOnlyConfig';
import {
  BrePostcardsPrintOnlyPay,
  BrePostcardsPrintOnlyRate
} from '~/tenants/bre/performable/print/postcards/printonly/BrePostcardsPrintOnlyData';
import { BreProviderId } from '~/tenants/bre/model/BreProvider';
import { datetime } from '~/lib/datettime';
import BrePhotosConfig from '~/tenants/bre/performable/photos/BrePhotosConfig';
import BreAerialConfig from '~/tenants/bre/performable/aerial/BreAerialConfig';

export default class BrePostcardsPrintOnlyJob extends BreJob<typeof BrePostcardsPrintOnlyConfig> {
  get performable(): typeof BrePostcardsPrintOnlyConfig {
    return BrePostcardsPrintOnlyConfig;
  }

  get assignee(): string | null {
    return BreProviderId.PRINT_TEAM.toString();
  }

  info(): DisplayData[] {
    const info = super.info();

    info.push({
      name: 'Count',
      value: this.metadata.count,
      provider: true,
    });

    if (this.metadata.design) {
      info.push({
        name: 'Design',
        value: this.metadata.design,
        provider: true,
      });
    }

    info.push({
      name: 'Need By',
      value: datetime(this.metadata.need_by, this.order.location.timezone).toFormat('LLL dd, yyyy'),
      provider: true,
    });

    return info;
  }

  get dependencies(): string[] {
    return this.isPrint() ? [BrePhotosConfig.id, BreAerialConfig.id] : [];
  }

  get configurable(): boolean {
    return true;
  }

  get eligible(): boolean {
    return this.isOneOf(BreOrderType.RESIDENTIAL, BreOrderType.COMMERCIAL, BreOrderType.PROPERTY_MKTG);
  }

  isDelivery(): boolean {
    return true;
  }

  isPrint(): boolean {
    return true;
  }

  defaultValue(): ZodVersionedMetadata<(typeof BrePostcardsPrintOnlyConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
      count: '50',
      need_by: datetime(this.order.created, this.order.timezone).toSQLDate(),
    };
  }

  revenueLines(): TenantJobLine[] {
    const { count, design } = this.metadata;
    const rate = BrePostcardsPrintOnlyRate[count];
    const lines: TenantJobLine[] = [];

    if (rate) {
      lines.push({
        id: 'postcards_printonly',
        description: `Print-only Post Cards (x${count})`,
        amount: new Big(rate),
      });
    }

    if (design) {
      const designRate = BreDesignRate[design].postcard_half;

      if (designRate) {
        lines.push({
          id: 'porcard_printonly_design',
          description: `Postcard design (${design} layout)`,
          amount: new Big(designRate),
        });
      }
    }

    return lines;
  }

  expenseLines(): TenantJobLine[] {
    const { count, design } = this.metadata;
    const pay = BrePostcardsPrintOnlyPay[count];
    const lines: TenantJobLine[] = [];

    if (pay) {
      lines.push({
        id: 'postcards_printonly',
        description: `Print-only Post Cards (x${count})`,
        amount: new Big(pay),
        taxable: true,
      });
    }

    if (design) {
      const designPay = BreDesignPay[design].postcard_half;

      if (designPay) {
        lines.push({
          id: 'porcard_printonly_design',
          description: `Postcard design (${design} layout)`,
          amount: new Big(designPay),
        });
      }
    }

    return lines;
  }
}
